import { Link, useParams, Navigate } from "react-router-dom";
import {
  Shield, Car, Video, DoorOpen, CalendarCheck, ShoppingBag, HardHat,
  KeyRound, UserCheck, Building2, MapPin, ChevronRight,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqBlock, CtaBand, Breadcrumbs } from "@/components/Blocks";
import {
  CITIES, SERVICES, COUNTIES, getCounty, getCity, PHONE, EMAIL,
  breadcrumbLd, faqLd, IMAGES,
} from "@/data/contentEngine";

const SERVICE_ICONS = {
  "manned-guarding": Shield,
  "mobile-patrols": Car,
  "cctv-monitoring": Video,
  "door-supervision": DoorOpen,
  "event-security": CalendarCheck,
  "retail-security": ShoppingBag,
  "construction-site-security": HardHat,
  "key-holding-alarm-response": KeyRound,
  "close-protection": UserCheck,
  "corporate-security": Building2,
};

const countyFaqs = (county, cities) => [
  {
    q: `Which areas of ${county.name} do you cover?`,
    a: `Every town and rural district in ${county.name} — including ${cities.map((c) => c.name).join(", ")} and everywhere in between. Multi-site cover across the county is available on a single contract.`,
  },
  {
    q: `Are your ${county.name} security officers SIA licensed?`,
    a: "Yes. Every frontline officer holds a valid SIA licence, is vetted to BS7858 standard and is site-briefed before deployment. Licence details are supplied with every contract.",
  },
  {
    q: `What security services are available in ${county.name}?`,
    a: `The full range: manned guarding, mobile patrols, CCTV monitoring, door supervision, event security, retail security, construction site security, key holding & alarm response, close protection and corporate security — all delivered by locally based officers.`,
  },
  {
    q: `How do I get a security quotation in ${county.name}?`,
    a: `Call ${PHONE} or email ${EMAIL}. We arrange a free risk assessment of your premises and provide a tailored quotation — emergency cover can often begin the same day.`,
  },
];

const CountyPage = () => {
  const { countySlug } = useParams();
  const county = getCounty(countySlug);
  if (!county) return <Navigate to="/cities" replace />;

  const cities = county.cities.map((s) => getCity(s)).filter(Boolean);
  const faqs = countyFaqs(county, cities);
  const otherCounties = COUNTIES.filter((c) => c.slug !== county.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: `Security Guard Services ${county.name}`,
        serviceType: "Security Services",
        provider: {
          "@type": "LocalBusiness",
          name: "Xtreme Protection — Northern Ireland Security Services",
          url: "https://northernirelandsecurity.co.uk",
          telephone: PHONE,
          email: EMAIL,
        },
        areaServed: { "@type": "AdministrativeArea", name: county.name },
        description: county.intro,
      },
      breadcrumbLd([
        { label: "Home", to: "/" },
        { label: "Areas We Cover", to: "/cities" },
        { label: county.name, to: `/counties/${county.slug}` },
      ]),
      faqLd(faqs),
    ],
  };

  return (
    <>
      <Seo
        title={`Security Company ${county.name} | SIA Security Guards | Xtreme Protection`}
        description={`SIA-licensed security guards across ${county.name}, Northern Ireland. Manned guarding, mobile patrols, CCTV monitoring & alarm response in ${cities.slice(0, 3).map((c) => c.name).join(", ")} & beyond. Call +44 2046 402840.`}
        path={`/counties/${county.slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow={`Security Company · ${county.name}`}
        title={`Security Guard Services in ${county.name}`}
        sub={county.intro}
        image={IMAGES.belfastArch}
        testId="county-hero"
      />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="county-content">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Areas We Cover", to: "/cities" },
            { label: county.name, to: null },
          ]}
        />
        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead
              eyebrow="Local Risk Landscape"
              title={`Security challenges across ${county.name}`}
              testId="county-local-heading"
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-300">{county.local}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <h2 className="mt-12 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="county-cities-heading">
                Towns & cities covered in {county.name}
              </h2>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="county-cities-list">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/cities/${c.slug}`}
                  data-testid={`county-city-${c.slug}`}
                  className="tactical-card group flex items-center gap-3 rounded-md px-4 py-3.5"
                >
                  <MapPin className="h-5 w-5 shrink-0 text-amber-500" />
                  <span className="flex-1 font-display text-base font-bold uppercase tracking-wide text-slate-100">
                    Security Guards {c.name}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                </Link>
              ))}
            </div>
            <Reveal delay={0.1}>
              <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="county-faq-heading">
                Security in {county.name} — FAQs
              </h2>
            </Reveal>
            <div className="mt-4">
              <FaqBlock faqs={faqs} testId="county-faq" />
            </div>
          </div>
          <aside>
            <Reveal>
              <div className="rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="county-others-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  Other counties we cover
                </h2>
                <ul className="mt-4 space-y-1.5">
                  {otherCounties.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/counties/${c.slug}`}
                        data-testid={`county-other-${c.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        Security Company {c.name}
                        <ChevronRight className="h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>

        <div className="mt-16">
          <SectionHead
            eyebrow={`Security Services in ${county.name}`}
            title={`Every security service, available across ${county.name}`}
            testId="county-services-heading"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.slug];
              const firstCity = cities[0];
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`/services/${s.slug}/${firstCity.slug}`}
                    data-testid={`county-service-${s.slug}`}
                    className="tactical-card group flex h-full items-center gap-4 rounded-md p-5"
                  >
                    <Icon className="h-6 w-6 shrink-0 text-amber-500" />
                    <span className="flex-1 font-display text-base font-bold uppercase tracking-wide text-slate-100">
                      {s.title}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Choose a service to see dedicated pages for {cities.map((c) => c.name).join(", ")} and the rest of Northern Ireland.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <CtaBand
          title={`Get protected anywhere in ${county.name}`}
          sub={`Free risk assessment for any premises in ${county.name} — call our control room or email the team for a tailored quotation.`}
        />
      </section>
    </>
  );
};

export default CountyPage;
